import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Badge, ProgressBar, Alert, Spinner, Modal, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { oneKlicks } from '../SDK/oneclick';
import { toast } from 'react-toastify';
import { getuserdata } from '../services/apiService';
import './StaffDashboard.css';
import { useAuth } from '../context/AuthContext';


const StaffDashboard = () => {
    const { userData } = useAuth();
    const [user, setUser] = useState(null);
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activityData, setActivityData] = useState({
        todayWorkTime: 0,
        todayBreakTime: 0,
        todayIdleTime: 0,
        productivityScore: 0,
        isOnline: false,
        lastActivity: null
    });
    const [showBreakModal, setShowBreakModal] = useState(false);
    const [breakReason, setBreakReason] = useState('');
    const [isOnBreak, setIsOnBreak] = useState(false);
    const [breakStartTime, setBreakStartTime] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        checkAuthentication();
        loadUserData();
        startActivityTracking();
    }, []);

    const checkAuthentication = async () => {
        const userData = await getuserdata("full");

        console.log("userData staff dashboard:", userData);
        if (!userData) {
            navigate('/staff/login');
            return;
        }
        
        // try {
        //     const parsedUser = JSON.parse(userData);
        //     setUser(parsedUser.user);
        //     setEmployee(parsedUser.employee);
        // } catch (error) {
        //     console.error('Error parsing user data:', error);
        //     navigate('/staff/login');
        // }
    };

    const loadUserData = async () => {
        try {
            if (!user) return;

            // Get today's activity data
            const today = new Date().toISOString().split('T')[0];
            
            // Get time tracking data
            const timeTrackingResponse = await oneKlicks.read('', 'user_time_tracking', {
                employee_id: user.id,
                date: today
            }, 'id DESC', {}, {});

            if (timeTrackingResponse.success && timeTrackingResponse.data.length > 0) {
                const timeData = timeTrackingResponse.data[0];
                setActivityData(prev => ({
                    ...prev,
                    todayWorkTime: timeData.total_work_time || 0,
                    todayBreakTime: timeData.total_break_time || 0,
                    todayIdleTime: timeData.total_idle_time || 0,
                    productivityScore: timeData.productivity_score || 0,
                    isOnline: true
                }));
            }

            // Get recent activity
            const activityResponse = await oneKlicks.read('', 'user_activity_logs', {
                employee_id: user.id
            }, 'created_at DESC', {}, { limit: 1 });

            if (activityResponse.success && activityResponse.data.length > 0) {
                setActivityData(prev => ({
                    ...prev,
                    lastActivity: activityResponse.data[0].created_at
                }));
            }

        } catch (error) {
            console.error('Error loading user data:', error);
        } finally {
            setLoading(false);
        }
    };

    const startActivityTracking = () => {
        // Log login activity
        logActivity('login', 'Staff dashboard accessed');
        
        // Update online status
        setActivityData(prev => ({ ...prev, isOnline: true }));
        
        // Start periodic activity updates
        const interval = setInterval(() => {
            updateActivityStatus();
        }, 30000); // Update every 30 seconds

        return () => clearInterval(interval);
    };

    const updateActivityStatus = async () => {
        try {
            if (!user) return;

            // Log active status
            await logActivity('active', 'User is active');
            
            // Update work time (simplified calculation)
            const currentTime = new Date();
            const workStartTime = new Date(currentTime.getFullYear(), currentTime.getMonth(), currentTime.getDate(), 9, 0, 0);
            const workTime = Math.max(0, (currentTime - workStartTime) / (1000 * 60)); // minutes
            
            setActivityData(prev => ({
                ...prev,
                todayWorkTime: Math.floor(workTime),
                lastActivity: currentTime.toISOString()
            }));

        } catch (error) {
            console.error('Error updating activity status:', error);
        }
    };

    const logActivity = async (activityType, description) => {
        try {
            if (!user) return;

            const activityData = {
                employee_id: user.id,
                activity_type: activityType,
                description: description,
                ip_address: '127.0.0.1',
                created_at: new Date().toISOString()
            };

            await oneKlicks.create('', 'user_activity_logs', activityData, {});
        } catch (error) {
            console.error('Error logging activity:', error);
        }
    };

    const handleBreakStart = async () => {
        setShowBreakModal(true);
    };

    const handleBreakEnd = async () => {
        if (isOnBreak && breakStartTime) {
            const breakDuration = Math.floor((new Date() - new Date(breakStartTime)) / (1000 * 60));
            
            // Update break time
            setActivityData(prev => ({
                ...prev,
                todayBreakTime: prev.todayBreakTime + breakDuration
            }));

            await logActivity('break_end', `Break ended after ${breakDuration} minutes`);
            setIsOnBreak(false);
            setBreakStartTime(null);
            toast.success('Break ended successfully');
        }
    };

    const handleBreakSubmit = async () => {
        if (!breakReason.trim()) {
            toast.error('Please provide a reason for the break');
            return;
        }

        await logActivity('break_start', `Break started: ${breakReason}`);
        setIsOnBreak(true);
        setBreakStartTime(new Date());
        setShowBreakModal(false);
        setBreakReason('');
        toast.success('Break started successfully');
    };

    const handleLogout = async () => {
        await logActivity('logout', 'Staff logout');
        localStorage.removeItem('staff_token');
        localStorage.removeItem('staff_user');
        navigate('/staff/login');
        toast.success('Logged out successfully');
    };

    const formatDuration = (minutes) => {
        if (!minutes) return '0m';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    };

    const getProductivityColor = (score) => {
        if (score >= 80) return 'success';
        if (score >= 60) return 'warning';
        if (score >= 40) return 'info';
        return 'danger';
    };

    if (loading) {
        return (
            <div className="staff-dashboard-loading">
                <Spinner animation="border" />
                <p className="mt-3">Loading dashboard...</p>
            </div>
        );
    }

    return (
        <div className="staff-dashboard">
            <div className="staff-dashboard-header">
                <div className="container-fluid">
                    <Row className="align-items-center">
                        <Col md={6}>
                            <h2 className="mb-0">Staff Dashboard</h2>
                            <p className="text-muted mb-0">
                                Welcome back, {user?.first_name} {user?.last_name}
                            </p>
                        </Col>
                        <Col md={6} className="text-end">
                            <div className="d-flex align-items-center justify-content-end gap-3">
                                <Badge bg={activityData.isOnline ? 'success' : 'secondary'} className="fs-6">
                                    <i className="fas fa-circle me-1"></i>
                                    {activityData.isOnline ? 'Online' : 'Offline'}
                                </Badge>
                                <Button variant="outline-danger" onClick={handleLogout}>
                                    <i className="fas fa-sign-out-alt me-1"></i>
                                    Logout
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            <div className="staff-dashboard-content">
                <div className="container-fluid py-4">
                    {/* Employee Info Card */}
                    <Card className="mb-4">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={8}>
                                    <h4 className="mb-1">{user?.first_name} {user?.last_name}</h4>
                                    <p className="text-muted mb-1">
                                        <strong>Employee ID:</strong> {employee?.employee_id}
                                    </p>
                                    <p className="text-muted mb-0">
                                        <strong>Department:</strong> {employee?.department} | 
                                        <strong> Designation:</strong> {employee?.designation}
                                    </p>
                                </Col>
                                <Col md={4} className="text-end">
                                    <div className="employee-status">
                                        <Badge bg="primary" className="fs-6">
                                            {employee?.employment_type?.replace('_', ' ').toUpperCase()}
                                        </Badge>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Today's Activity Summary */}
                    <Row className="mb-4">
                        <Col md={3}>
                            <Card className="text-center activity-card">
                                <Card.Body>
                                    <div className="activity-icon work-time">
                                        <i className="fas fa-clock"></i>
                                    </div>
                                    <h3 className="mt-3 mb-1">{formatDuration(activityData.todayWorkTime)}</h3>
                                    <p className="text-muted mb-0">Work Time</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="text-center activity-card">
                                <Card.Body>
                                    <div className="activity-icon break-time">
                                        <i className="fas fa-coffee"></i>
                                    </div>
                                    <h3 className="mt-3 mb-1">{formatDuration(activityData.todayBreakTime)}</h3>
                                    <p className="text-muted mb-0">Break Time</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="text-center activity-card">
                                <Card.Body>
                                    <div className="activity-icon idle-time">
                                        <i className="fas fa-pause"></i>
                                    </div>
                                    <h3 className="mt-3 mb-1">{formatDuration(activityData.todayIdleTime)}</h3>
                                    <p className="text-muted mb-0">Idle Time</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="text-center activity-card">
                                <Card.Body>
                                    <div className="activity-icon productivity">
                                        <i className="fas fa-chart-line"></i>
                                    </div>
                                    <h3 className="mt-3 mb-1">{activityData.productivityScore}%</h3>
                                    <p className="text-muted mb-0">Productivity</p>
                                    <ProgressBar 
                                        now={activityData.productivityScore} 
                                        variant={getProductivityColor(activityData.productivityScore)}
                                        className="mt-2"
                                    />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Break Management */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">Break Management</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={8}>
                                    <p className="mb-0">
                                        {isOnBreak ? (
                                            <span className="text-warning">
                                                <i className="fas fa-coffee me-2"></i>
                                                Currently on break since {new Date(breakStartTime).toLocaleTimeString()}
                                            </span>
                                        ) : (
                                            <span className="text-success">
                                                <i className="fas fa-check-circle me-2"></i>
                                                Ready to work
                                            </span>
                                        )}
                                    </p>
                                </Col>
                                <Col md={4} className="text-end">
                                    {isOnBreak ? (
                                        <Button variant="success" onClick={handleBreakEnd}>
                                            <i className="fas fa-play me-1"></i>
                                            End Break
                                        </Button>
                                    ) : (
                                        <Button variant="warning" onClick={handleBreakStart}>
                                            <i className="fas fa-pause me-1"></i>
                                            Start Break
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Activity Status */}
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Activity Status</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <p><strong>Last Activity:</strong> {
                                        activityData.lastActivity 
                                            ? new Date(activityData.lastActivity).toLocaleString()
                                            : 'No recent activity'
                                    }</p>
                                </Col>
                                <Col md={6}>
                                    <p><strong>Status:</strong> 
                                        <Badge bg={activityData.isOnline ? 'success' : 'secondary'} className="ms-2">
                                            {activityData.isOnline ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </p>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/* Break Modal */}
            <Modal show={showBreakModal} onHide={() => setShowBreakModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Start Break</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Break Reason</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={breakReason}
                                onChange={(e) => setBreakReason(e.target.value)}
                                placeholder="Please specify the reason for your break..."
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowBreakModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="warning" onClick={handleBreakSubmit}>
                        Start Break
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StaffDashboard;
